import { getUrlParams, parseJson, commonParseQuery } from './common-utils';
import { getPlaceDetailsServerSide } from '../../dweb-cars/shared/pages/SeoPage/redux/thunks';

/**
 * Parses the Review URL and returns parsed data as an object
 * Similar to parseCarsSrpUrl but for review page
 * Only s_n, s_i, d_n, d_i come from URL - rest fetched from getPlaceDetails API
 * @param {object} params - The query params object
 * @returns {Promise<Object|null>} - Parsed data object or null if category_id is missing
 */
export async function parseCarsReviewUrl(url) {
  const searchParams = getUrlParams(url);
  const { qryParams = '{}' } = searchParams;
  const parsedJson = parseJson(qryParams); 

  const {
      s_n = '',           // source name
      s_i = '',           // source place_id
      d_n = '',           // destination name
      d_i = '',           // destination place_id
      s_t = '',           // start time
      e_t = '',           // end time
      t_t = 'ONE_WAY',    // trip type
      package_id = '',    // for rental/hr trips
      flow = '',
      isSeo = false,
      stops = [],
      category_id = '',   // category id - required for redirect           // vehicle key (fallback for category_id)
  } = parsedJson;
  
  // Return null if category_id is not present - don't navigate to new review
  if (!category_id?.length) {
      return null;
  }

  const isRental = !!(t_t === 'LOCAL_RENTAL' || t_t === 'HOURLY_RENTAL');
  const isAirport = !!(t_t === 'AIRPORT');

  // Initialize source and destination with basic data from URL
  let sourceData = {
      name: s_n,
      place_id: s_i,
      city_name: '',
      state: '',
      address: '',
      latitude: '',
      longitude: '',
      pincode: '',
      google_city: '',
      is_city: false,
      is_airport: isAirport
  };

  let destinationData = {
      name: d_n,
      place_id: d_i,
      city_name: '',
      state: '',
      address: '',
      latitude: '',
      longitude: '',
      pincode: '',
      google_city: '',
      is_city: false,
      is_airport: isAirport
  };

  // Fetch source place details using place_id
  if (s_i) {
      try {
          const sourcePlaceDetails = await getPlaceDetailsServerSide(s_i);
          sourceData = {
              name: s_n || sourcePlaceDetails?.address || '',
              place_id: s_i,
              city_name: sourcePlaceDetails?.city || '',
              state: sourcePlaceDetails?.state || '',
              address: sourcePlaceDetails?.address || '',
              latitude: sourcePlaceDetails?.latitude || '',
              longitude: sourcePlaceDetails?.longitude || '',
              pincode: sourcePlaceDetails?.pincode || '',
              google_city: sourcePlaceDetails?.google_city || '',
              is_city: sourcePlaceDetails?.is_city || false,
              is_airport: sourcePlaceDetails?.is_airport || isAirport
          };
      } catch (error) {
          console.warn('Failed to fetch source place details:', error);
      }
  }

  // Fetch destination place details using place_id (skip for rental trips)
  if (d_i && !isRental) {
      try {
          const destinationPlaceDetails = await getPlaceDetailsServerSide(d_i);
          destinationData = {
              name: d_n || destinationPlaceDetails?.address || '',
              place_id: d_i,
              city_name: destinationPlaceDetails?.city || '',
              state: destinationPlaceDetails?.state || '',
              address: destinationPlaceDetails?.address || '',
              latitude: destinationPlaceDetails?.latitude || '',
              longitude: destinationPlaceDetails?.longitude || '',
              pincode: destinationPlaceDetails?.pincode || '',
              google_city: destinationPlaceDetails?.google_city || '',
              is_city: destinationPlaceDetails?.is_city || false,
              is_airport: destinationPlaceDetails?.is_airport || isAirport
          };
      } catch (error) {
          console.warn('Failed to fetch destination place details:', error);
      }
  }

  // Determine rf value
  const rfValue = (t_t === 'ROUND_TRIP' ? 'rt' : t_t === 'AIRPORT' ? 'at' : isRental ? 'hr' : 'ow');
  const { newSDate, newSTime, newEDate, newETime } = commonParseQuery({ s_t, e_t, off: 0, r_off: 0 });

  // Build parsed data object
  const parseData = {
    startDate: {
      date: newSDate,
      time: newSTime[0]
    },
    endDate: {
      date: newEDate,
      time: newETime[1]
    },
    source: sourceData,
    destination: destinationData,
    tripType: {
        name: t_t,
        rf: rfValue
    },
    flow: flow || 'direct-cabs',
    isSeo: !!isSeo,
    package_id,
    category_id: category_id,
    stopOvers: [],
  };

  // Process stops/stopovers if present
  if (stops?.length) {
      const hasStopsMissingCoords = stops.some(details => 
          !details?.value?.latitude && !details?.value?.longitude
      );

      let newStopOvers;

      if (hasStopsMissingCoords) {
          newStopOvers = await Promise.all(stops.map(async (details) => {
              let stopValue = {
                  place_id: details?.value?.place_id,
                  city_name: details?.value?.region || details?.value?.city_name || '',
                  name: details?.value?.description,
                  address: details?.value?.address,
                  latitude: details?.value?.latitude,
                  longitude: details?.value?.longitude,
                  pincode: details?.value?.pincode,
                  google_city: details?.value?.google_city,
                  is_city: details?.value?.is_city,
                  is_airport: details?.value?.is_airport
              };

              // Fetch place details if latitude/longitude missing
              if ((!details?.value?.latitude || !details?.value?.longitude) && details?.value?.place_id) {
                  try {
                      const stopPlaceDetails = await getPlaceDetailsServerSide(details?.value?.place_id);
                      stopValue = { ...stopValue, ...stopPlaceDetails };
                  } catch (error) {
                      console.warn('Failed to fetch stop place details:', error);
                  }
              }

              return {
                  id: details?.id,
                  value: stopValue
              };
          }));
      } else {
          newStopOvers = stops.map(details => ({
              id: details?.id,
              value: {
                  place_id: details?.value?.place_id,
                  city_name: details?.value?.region || details?.value?.city_name || '',
                  name: details?.value?.description,
                  address: details?.value?.address,
                  latitude: details?.value?.latitude,
                  longitude: details?.value?.longitude,
                  pincode: details?.value?.pincode,
                  google_city: details?.value?.google_city,
                  is_city: details?.value?.is_city,
                  is_airport: details?.value?.is_airport
              }
          }));
      }

      parseData.stopOvers = newStopOvers;
  }

  return parseData;
}

export const parseReviewPageUrlToConfig = (url) => {
  const searchParams = getUrlParams(url);
  const { qryParams = '{}' } = searchParams;
  const parsedJson = parseJson(qryParams); 
  const { v_d = "", s_d = "", s_i = "", s_n = "", d_i = "", d_n = "", s_t = "", e_t = "", t_t = "", rf = "", v_t = "", off = 0, r_off = 0, pc = "", flow = "", isSeo = false,is_ryde, package_id = '', packageTime = '', stops } = parsedJson;
  const { newSDate, newSTime, newEDate, newETime } = commonParseQuery({ s_t, e_t, off, r_off });
  const parseData = {
    key: v_d,
    dispatchSearchId: s_d,
    vehicle_type: v_t,
    source: {
      place_id: s_i,
      name: s_n,
    },
    destination: {
      place_id: d_i,
      name: d_n
    },
    startDate: {
      date: newSDate,
      time: newSTime[0]
    },
    endDate: {
      date: newEDate,
      time: newETime[1]
    },
    tripType: {
      name: e_t || r_off ? 'ROUND_TRIP' : t_t,
      rf: e_t || r_off ? 'rt' : rf
    },
    package_id,
    packageTime,
    isSeo: !!(s_n && s_i && d_i && d_n && t_t) || !!isSeo,
    pc,
    flow,
    is_ryde
  };

  if (stops?.length) {
    const newStopOvers = stops.map(details => {
      return {
        id: details?.id,
        value: {
          place_id: details?.value?.place_id,
          city_name: details?.value?.region,
          name: details?.value?.description
        }
      }
    })
    parseData['stopOvers'] = newStopOvers;
    parseData['previousStopOvers'] = newStopOvers;
  }

  return parseData;
}

export default parseReviewPageUrlToConfig;